<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';

// Seletor de pipeline no cabeçalho do board: botão com o nome do pipeline atual
// + chevron; ao abrir mostra um input de busca (autocomplete client-side) e a
// lista dos pipelines da conta. Selecionar um pipeline diferente emite `select`
// com o id — o board cuida da navegação. Mantemos a raiz como um <div> ESTÁVEL
// (sem v-if no nó raiz) e usamos onClickOutside para fechar.
const props = defineProps({
  // Lista de pipelines da conta: [{ id, name }, ...].
  pipelines: {
    type: Array,
    default: () => [],
  },
  // Id do pipeline atualmente aberto (number).
  currentId: {
    type: [Number, String],
    default: null,
  },
  // Nome atual (vem do board) — fallback enquanto a lista de pipelines carrega,
  // evitando o flash de "sem título".
  currentName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const isOpen = ref(false);
const query = ref('');
const rootRef = ref(null);
const inputRef = ref(null);

const currentIdNumber = computed(() => Number(props.currentId));

const currentPipeline = computed(
  () =>
    props.pipelines.find(pipeline => pipeline.id === currentIdNumber.value) ||
    null
);

// Rótulo do botão: nome do pipeline atual ou um fallback enquanto a lista carrega.
const currentLabel = computed(
  () =>
    currentPipeline.value?.name ||
    props.currentName ||
    t('PIPELINES.SWITCHER.UNTITLED')
);

// Filtro client-side pelo texto digitado (case-insensitive, acento-insensível
// via localeCompare base). Sem termo, devolve a lista completa.
const filteredPipelines = computed(() => {
  const term = query.value.trim().toLowerCase();
  if (!term) return props.pipelines;
  return props.pipelines.filter(pipeline =>
    (pipeline.name || '').toLowerCase().includes(term)
  );
});

// Abre o dropdown e foca o input de busca na sequência (após o render).
const open = async () => {
  isOpen.value = true;
  query.value = '';
  await nextTick();
  inputRef.value?.focus();
};

const close = () => {
  isOpen.value = false;
  query.value = '';
};

const toggle = () => {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

const onSelect = pipeline => {
  close();
  // Não re-navega para o mesmo pipeline (evita reload/flicker desnecessário).
  if (pipeline.id === currentIdNumber.value) return;
  emit('select', pipeline.id);
};

// Fecha ao clicar fora do seletor.
onClickOutside(rootRef, () => {
  if (isOpen.value) close();
});

// Se a lista de pipelines mudar enquanto o dropdown está aberto (carregamento
// assíncrono), mantemos o estado aberto — só garantimos o foco no input.
watch(isOpen, async value => {
  if (value) {
    await nextTick();
    inputRef.value?.focus();
  }
});
</script>

<template>
  <div ref="rootRef" class="relative">
    <!-- Botão: nome do pipeline atual + chevron -->
    <button
      type="button"
      class="flex items-center h-8 gap-1.5 px-2.5 -ml-1 text-xl font-medium rounded-lg text-n-slate-12 hover:bg-n-alpha-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
      :aria-expanded="isOpen"
      :aria-label="t('PIPELINES.SWITCHER.LABEL')"
      data-test-id="pipeline-switcher-trigger"
      @click="toggle"
    >
      <span class="max-w-[16rem] truncate">{{ currentLabel }}</span>
      <span
        class="flex-shrink-0 transition-transform size-5 i-lucide-chevron-down text-n-slate-11"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <!-- Dropdown: input de busca + lista filtrada -->
    <div
      v-if="isOpen"
      class="absolute top-full z-50 mt-1 overflow-hidden rounded-lg shadow-lg w-72 bg-n-solid-2 outline outline-1 outline-n-weak ltr:left-0 rtl:right-0"
      data-test-id="pipeline-switcher-dropdown"
    >
      <div class="p-2 border-b border-n-weak">
        <div class="relative">
          <span
            class="absolute -translate-y-1/2 pointer-events-none top-1/2 size-4 i-lucide-search text-n-slate-10 ltr:left-3 rtl:right-3"
          />
          <input
            ref="inputRef"
            v-model="query"
            type="text"
            :placeholder="t('PIPELINES.SWITCHER.SEARCH_PLACEHOLDER')"
            class="w-full h-8 py-0 text-sm border rounded-md ltr:pl-9 rtl:pr-9 ltr:pr-3 rtl:pl-3 border-n-weak bg-n-alpha-2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
            data-test-id="pipeline-switcher-search"
          />
        </div>
      </div>
      <ul
        v-if="filteredPipelines.length"
        class="py-1 overflow-y-auto max-h-72"
        role="listbox"
      >
        <li v-for="pipeline in filteredPipelines" :key="pipeline.id">
          <button
            type="button"
            class="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-2 font-medium': pipeline.id === currentIdNumber,
            }"
            role="option"
            :aria-selected="pipeline.id === currentIdNumber"
            data-test-id="pipeline-switcher-option"
            @click="onSelect(pipeline)"
          >
            <span class="min-w-0 truncate">{{ pipeline.name }}</span>
            <span
              v-if="pipeline.id === currentIdNumber"
              class="flex-shrink-0 ltr:ml-auto rtl:mr-auto size-4 i-lucide-check text-n-brand"
            />
          </button>
        </li>
      </ul>
      <div v-else class="px-3 py-3 text-sm text-n-slate-11">
        {{ t('PIPELINES.SWITCHER.NO_RESULTS') }}
      </div>
    </div>
  </div>
</template>
